import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
} from '@react-pdf/renderer'
import { IMerchant } from '../../../types/merchant'
import { EOrderType, IOrder, IOrderItem } from '../../../types/order'
import SarabunFont from '../../../assets/fonts/th-sarabun-new.ttf'
import SarabunFontBold from '../../../assets/fonts/th-sarabun-bold.ttf'
import dayjs from 'dayjs'
import { numberFormat } from '../../../utils'
import { VAT_TYPES } from '../../../constants/global'
import { sumBy } from 'lodash'

Font.register({ family: 'Sarabun', src: SarabunFont })
Font.register({ family: 'Sarabun', src: SarabunFontBold, fontWeight: 'bold' })

interface Props {
  orders: IOrder[]

  merchant?: any | IMerchant
  vatType?: VAT_TYPES
  size?: 'A4' | '80mm'
  showDate?: string
}

const sizes = {
  A4: {
    width: 8.27,
    height: 11.69,
  },
  '80mm': {
    width: 3.15,
    height: 11.69,
  },
}

const PDFTrackingOrdersSummary = (props: Props) => {
  const {
    orders,
    merchant,
    vatType = VAT_TYPES.NON_VAT,
    size = 'A4',
    showDate = 'order',
  } = props
  const fontSize = size === 'A4' ? 14 : 10
  const paperPadding = size === 'A4' ? 20 : 8
  const vat = 0.07
  const paperSize = sizes[size] || sizes.A4

  const itemValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const vatValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value - value / 1.07
      case 'exclude':
        return value * vat
      default:
        return 0
    }
  }

  const subTotalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value / 1.07
      case 'exclude':
        return value
      default:
        return value
    }
  }

  const totalValue = (value: number) => {
    switch (vatType) {
      case 'include':
        return value
      case 'exclude':
        return value * vat + value
      default:
        return value
    }
  }

  const orderItemsAll: IOrderItem[] = orders.map((order) => order.items).flat()
  const productsReduce = orderItemsAll.reduce(
    (acc, item) => {
      if (!acc[item.productCode]) {
        acc[item.productCode] = {
          productCode: item.productCode,
          productName: item.productName,
          total: 0,
        }
      }
      acc[item.productCode].total += item.quantity
      return acc
    },
    {} as Record<string, any>,
  )
  //sorting total max to min
  const orderItems = Object.values(productsReduce).sort(
    (a, b) => b.total - a.total,
  )

  // const orderTotal = orderItems.length
  // const orderTotalUnit = sumBy(orderItems, 'total')

  return (
    <Document>
      <Page
        wrap={true}
        size={[paperSize.width * 72, paperSize.height * 72]}
        style={{
          flexDirection: 'row',
          fontFamily: 'Sarabun',
          padding: paperPadding,
        }}
      >
        <View
          style={{
            flex: 1,
            fontSize,
            lineHeight: 1,
          }}
        >
          <Text
            style={{
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: 22,
              marginBottom: 15,
            }}
          >
            ใบแพ็คสินค้า
          </Text>
          {orders.map((order: IOrder, index: number) => {
            const orderTotal = order.items.length
            const orderTotalUnit = sumBy(order.items, 'quantity')

            return (
              <View key={`order-${index}`} wrap={false}>
                <Text
                  style={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    fontSize: 16,
                    marginBottom: 5,
                  }}
                >
                  วันที่ {dayjs().format('DD/MM/YYYY')}
                </Text>
                <View
                  style={{
                    alignItems: 'flex-start',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    fontWeight: 'bold',
                    fontSize: 14,
                    marginBottom: 5,
                  }}
                >
                  <View>
                    <Text style={{ textAlign: 'left' }}>
                      ชื่อผู้รับ : {order.recipient.name} ({order.code})
                    </Text>
                    <Text style={{ textAlign: 'left' }}>
                      โทร : {order.recipient.phone}
                    </Text>
                  </View>
                  <View
                    style={{
                      textAlign: 'right',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Text style={{ textAlign: 'right' }}>
                      จำนวนสินค้าทั้งหมด: {orderTotal} รายการ/ {orderTotalUnit}{' '}
                      ชิ้น
                    </Text>
                    <Text style={{ textAlign: 'right' }}>
                      หมายเลขพัสดุ : {order.trackingCode ?? '-'}
                    </Text>
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: 'column',
                    fontSize: 13,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      fontWeight: 'bold',
                      ...TableStyle.b,
                    }}
                  >
                    <Text
                      style={{
                        width: '10%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      ลำดับ
                    </Text>
                    <Text
                      style={{
                        width: '45%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      รายการ
                    </Text>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      ยอดรวม
                    </Text>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lt,
                        ...TableStyle.header,
                      }}
                    >
                      รหัสสินค้า
                    </Text>
                    <Text
                      style={{
                        width: '15%',
                        ...TableStyle.lrt,
                        ...TableStyle.header,
                      }}
                    >
                      จำนวน
                    </Text>
                  </View>
                  {order.items.map((item: IOrderItem, itemIndex: number) => (
                    <>
                      <View
                        key={`item-${itemIndex}`}
                        style={{
                          flexDirection: 'row',
                          ...TableStyle.b,
                        }}
                      >
                        <Text
                          style={{
                            width: '10%',
                            ...TableStyle.l,
                            ...TableStyle.header,
                          }}
                        >
                          {itemIndex + 1}
                        </Text>
                        <View
                          style={{
                            width: '45%',
                            ...TableStyle.l,
                            ...TableStyle.header,
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <View>
                            <Text style={{ fontWeight: 'bold' }}>
                              {item.productName}
                            </Text>
                          </View>
                        </View>

                        <Text
                          style={{
                            width: '15%',
                            ...TableStyle.l,
                            ...TableStyle.header,
                          }}
                        >
                          {numberFormat(item.total)}
                        </Text>

                        <Text
                          style={{
                            width: '15%',
                            ...TableStyle.l,
                            ...TableStyle.header,
                          }}
                        >
                          {item.productCode}
                        </Text>
                        <Text
                          style={{
                            width: '15%',
                            ...TableStyle.lr,
                            ...TableStyle.header,
                          }}
                        >
                          {item.quantity}
                        </Text>
                      </View>
                      {item.isCollection &&
                        item.collections?.map((collection, collectionIndex) => (
                          <View
                            key={`product-coll-${collectionIndex}`}
                            wrap={false}
                          >
                            <View
                              key={`item-coll-${collectionIndex}`}
                              style={{
                                flexDirection: 'row',
                                ...TableStyle.b,
                              }}
                            >
                              <Text
                                style={{
                                  width: '10%',
                                  ...TableStyle.l,
                                  ...TableStyle.header,
                                }}
                              >
                                {''}
                              </Text>
                              <View
                                style={{
                                  width: '60%',
                                  ...TableStyle.l,
                                  ...TableStyle.header,
                                  textAlign: 'left',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Text style={{ paddingLeft: '5px' }}>
                                  {'- '}
                                  {collection.productName}
                                </Text>
                              </View>

                              <Text
                                style={{
                                  width: '15%',
                                  ...TableStyle.l,
                                  ...TableStyle.header,
                                }}
                              >
                                {collection.productCode}
                              </Text>
                              <Text
                                style={{
                                  width: '15%',
                                  ...TableStyle.lr,
                                  ...TableStyle.header,
                                }}
                              >
                                {numberFormat(collection.quantity)}
                              </Text>
                            </View>
                          </View>
                        ))}
                    </>
                  ))}
                </View>

                <View
                  style={{
                    ...Divide.divideBold,
                  }}
                ></View>
              </View>
            )
          })}
        </View>
      </Page>
    </Document>
  )
}

export default PDFTrackingOrdersSummary

const borderColor = '#333'
const borderDivideColor = '#aaa'

const Divide = StyleSheet.create({
  divide: {
    borderBottomWidth: 1,
    borderColor: borderDivideColor,
    borderStyle: 'dashed',
    marginTop: 15,
    marginBottom: 10,
  },
  divideBold: {
    borderBottomWidth: 2,
    borderColor: borderDivideColor,
    borderStyle: 'dashed',
    marginTop: 15,
    marginBottom: 10,
  },
})

const TableStyle = StyleSheet.create({
  header: {
    padding: 5,
    textAlign: 'center',
  },
  footer: {
    padding: 5,
    textAlign: 'right',
    backgroundColor: '#f0f0f0',
  },
  l: {
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  r: {
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lr: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  t: {
    borderTopWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  tb: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lt: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrt: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  b: {
    borderBottomWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lb: {
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  rb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
  lrb: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderColor,
    borderStyle: 'solid',
  },
})
